import { vaultService } from '@/services/vault.service';
import {
  CreateInvitationDto,
  CreateVaultDto,
  GrantFolderPermissionDto,
  GrantPermissionDto,
  InitializeVaultKeyDto,
  UnlockVaultDto,
  UpdateFolderPermissionDto,
  UpdateVaultDto,
  UploadDocumentDto,
  VaultAuditLogParams,
} from '@/types/vault.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

// Query Keys
export const vaultKeys = {
  all: ['vaults'] as const,
  lists: () => [...vaultKeys.all, 'list'] as const,
  list: (filters: string) => [...vaultKeys.lists(), { filters }] as const,
  details: () => [...vaultKeys.all, 'detail'] as const,
  detail: (id: string) => [...vaultKeys.details(), id] as const,
  folders: () => [...vaultKeys.all, 'folder'] as const,
  folder: (folderId: string) => [...vaultKeys.folders(), folderId] as const,
  employees: () => [...vaultKeys.all, 'employee'] as const,
  employee: (employeeId: string) =>
    [...vaultKeys.employees(), employeeId] as const,
  documents: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'documents'] as const,
  documentPeriods: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'documentPeriods'] as const,
  permissions: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'permissions'] as const,
  invitations: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'invitations'] as const,
  auditLogs: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'auditLogs'] as const,
  statistics: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'statistics'] as const,
  activity: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'activity'] as const,
  storageQuota: (vaultId: string) =>
    [...vaultKeys.detail(vaultId), 'storageQuota'] as const,
};

// ===== Vault Queries =====

export const useVaults = () => {
  return useQuery({
    queryKey: vaultKeys.lists(),
    queryFn: async () => {
      const response = await vaultService.listVaults();
      return response.data;
    },
  });
};

export const useVault = (vaultId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: vaultKeys.detail(vaultId),
    queryFn: async () => {
      const response = await vaultService.getVault(vaultId);
      return response.data;
    },
    enabled: enabled && !!vaultId,
  });
};

export const useVaultStatistics = (vaultId: string) => {
  return useQuery({
    queryKey: vaultKeys.statistics(vaultId),
    queryFn: async () => {
      const response = await vaultService.getVaultStatistics(vaultId);
      return response.data;
    },
    enabled: !!vaultId,
  });
};

// ===== Vault Mutations =====

export const useCreateVault = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVaultDto) => vaultService.createVault(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.lists() });
      toast.success(
        t('vault.messages.createSuccess', 'Coffre créé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t('vault.messages.createError', 'Erreur lors de la création du coffre');
      toast.error(message);
    },
  });
};

export const useUpdateVault = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateVaultDto) =>
      vaultService.updateVault(vaultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.detail(vaultId) });
      queryClient.invalidateQueries({ queryKey: vaultKeys.lists() });
      toast.success(
        t('vault.messages.updateSuccess', 'Coffre mis à jour avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.updateError',
          'Erreur lors de la mise à jour du coffre'
        );
      toast.error(message);
    },
  });
};

export const useInitializeVaultKey = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InitializeVaultKeyDto) =>
      vaultService.initializeVaultKey(vaultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.detail(vaultId) });
      toast.success(
        t('vault.messages.initializeSuccess', 'Coffre initialisé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.initializeError',
          "Erreur lors de l'initialisation du coffre"
        );
      toast.error(message);
    },
  });
};

export const useUnlockVault = (vaultId: string) => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: UnlockVaultDto) =>
      vaultService.unlockVault(vaultId, data),
    onSuccess: () => {
      toast.success(
        t('vault.messages.unlockSuccess', 'Coffre déverrouillé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.unlockError',
          'Erreur lors du déverrouillage du coffre'
        );
      toast.error(message);
    },
  });
};

/**
 * Hook pour récupérer les paramètres de chiffrement du vault
 * Utilisé pour le déverrouillage côté client
 */
export const useGetVaultEncryptionParams = (vaultId: string) => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => vaultService.getVaultEncryptionParams(vaultId),
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.getEncryptionParamsError',
          'Erreur lors de la récupération des paramètres de chiffrement'
        );
      toast.error(message);
    },
  });
};

export const useDeleteVault = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vaultId: string) => vaultService.deleteVault(vaultId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.lists() });
      toast.success(
        t('vault.messages.deleteSuccess', 'Coffre supprimé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.deleteError',
          'Erreur lors de la suppression du coffre'
        );
      toast.error(message);
    },
  });
};

// ===== Document Queries & Mutations =====

export const useVaultDocuments = (
  vaultId: string,
  enabled: boolean = true,
  params?: {
    page?: number;
    limit?: number;
    type?: string;
    search?: string;
    year?: number;
    month?: number;
  }
) => {
  return useQuery({
    queryKey: [...vaultKeys.documents(vaultId), params],
    queryFn: async () => {
      const response = await vaultService.listDocuments(vaultId, params);
      return response.data;
    },
    enabled: enabled && !!vaultId,
  });
};

/**
 * Hook pour récupérer les périodes disponibles pour le filtrage des documents
 */
export const useVaultDocumentPeriods = (
  vaultId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: vaultKeys.documentPeriods(vaultId),
    queryFn: async () => {
      const response = await vaultService.getDocumentPeriods(vaultId);
      return response.data;
    },
    enabled: enabled && !!vaultId,
  });
};

export const useUploadDocument = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UploadDocumentDto) => {
      console.log('[useUploadDocument] Starting upload mutation');
      try {
        const result = await vaultService.uploadDocument(vaultId, data);
        console.log('[useUploadDocument] Upload completed:', result);
        return result;
      } catch (error) {
        console.error('[useUploadDocument] Upload failed:', error);
        throw error;
      }
    },
    onSuccess: data => {
      console.log('[useUploadDocument] onSuccess triggered:', data);
      queryClient.invalidateQueries({ queryKey: vaultKeys.documents(vaultId) });
      queryClient.invalidateQueries({
        queryKey: vaultKeys.statistics(vaultId),
      });
      toast.success(
        t('vault.messages.uploadSuccess', 'Document téléversé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      console.error('[useUploadDocument] onError triggered:', error);
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.uploadError',
          'Erreur lors du téléversement du document'
        );
      toast.error(message);
    },
  });
};

export const useServerUploadDocument = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      file: File;
      documentType: string;
      year: number;
      month: number;
    }) => vaultService.serverUploadDocument(vaultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.documents(vaultId) });
      queryClient.invalidateQueries({
        queryKey: vaultKeys.statistics(vaultId),
      });
      toast.success(
        t('vault.messages.uploadSuccess', 'Document téléversé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.uploadError',
          'Erreur lors du téléversement du document'
        );
      toast.error(message);
    },
  });
};

export const useSelfUploadDocument = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      file: File;
      documentType: string;
      year?: number;
      month?: number;
    }) => vaultService.selfUploadDocument(vaultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.documents(vaultId) });
      queryClient.invalidateQueries({
        queryKey: vaultKeys.statistics(vaultId),
      });
      queryClient.invalidateQueries({
        queryKey: vaultKeys.storageQuota(vaultId),
      });
      toast.success(
        t('vault.messages.uploadSuccess', 'Document téléversé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.uploadError',
          'Erreur lors du téléversement du document'
        );
      toast.error(message);
    },
  });
};

export const useStorageQuota = (vaultId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: vaultKeys.storageQuota(vaultId),
    queryFn: async () => {
      const response = await vaultService.getStorageQuota(vaultId);
      return response.data;
    },
    enabled: enabled && !!vaultId,
  });
};

export const useDeleteDocument = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      vaultService.deleteDocument(vaultId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.documents(vaultId) });
      queryClient.invalidateQueries({
        queryKey: vaultKeys.statistics(vaultId),
      });
      toast.success(
        t('vault.messages.deleteDocumentSuccess', 'Document supprimé')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.deleteDocumentError',
          'Erreur lors de la suppression'
        );
      toast.error(message);
    },
  });
};

export const useMigrateDocument = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ documentId, file }: { documentId: string; file: Blob }) =>
      vaultService.migrateDocumentToServer(vaultId, documentId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.documents(vaultId) });
      queryClient.invalidateQueries({
        queryKey: vaultKeys.statistics(vaultId),
      });
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.migrateError',
          'Erreur lors de la migration du document'
        );
      toast.error(message);
    },
  });
};

// ===== Permissions Queries & Mutations =====

export const useVaultPermissions = (vaultId: string) => {
  return useQuery({
    queryKey: vaultKeys.permissions(vaultId),
    queryFn: async () => {
      const response = await vaultService.listPermissions(vaultId);
      return response.data;
    },
    enabled: !!vaultId,
  });
};

export const useGrantPermission = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GrantPermissionDto) =>
      vaultService.grantPermission(vaultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.permissions(vaultId),
      });
      toast.success(
        t('vault.messages.grantSuccess', 'Permission accordée avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.grantError',
          "Erreur lors de l'attribution de la permission"
        );
      toast.error(message);
    },
  });
};

export const useRevokePermission = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permissionId: string) =>
      vaultService.revokePermission(vaultId, permissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.permissions(vaultId),
      });
      toast.success(
        t('vault.messages.revokeSuccess', 'Permission révoquée avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.revokeError',
          'Erreur lors de la révocation de la permission'
        );
      toast.error(message);
    },
  });
};

// ===== Folder Granular Permissions =====

export const useGrantFolderPermission = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GrantFolderPermissionDto) =>
      vaultService.grantFolderPermission(vaultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.permissions(vaultId),
      });
      toast.success(
        t('vault.messages.grantSuccess', 'Permission accordée avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.grantError',
          "Erreur lors de l'attribution de la permission"
        );
      toast.error(message);
    },
  });
};

export const useSelfAddToVault = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => vaultService.selfAddToVault(vaultId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.permissions(vaultId),
      });
      toast.success(
        t(
          'vault.messages.selfAddSuccess',
          'Vous avez rejoint le coffre-fort avec toutes les permissions'
        )
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.selfAddError',
          'Erreur lors de la tentative de rejoindre le coffre-fort'
        );
      toast.error(message);
    },
  });
};

export const useUpdateFolderPermission = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      permissionId,
      data,
    }: {
      permissionId: string;
      data: UpdateFolderPermissionDto;
    }) => vaultService.updateFolderPermission(vaultId, permissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.permissions(vaultId),
      });
      toast.success(
        t(
          'vault.messages.updatePermissionSuccess',
          'Permission mise à jour avec succès'
        )
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.updatePermissionError',
          'Erreur lors de la mise à jour de la permission'
        );
      toast.error(message);
    },
  });
};

// ===== Invitations Queries & Mutations =====

export const useVaultInvitations = (vaultId: string) => {
  return useQuery({
    queryKey: vaultKeys.invitations(vaultId),
    queryFn: async () => {
      const response = await vaultService.listInvitations(vaultId);
      return response.data;
    },
    enabled: !!vaultId,
  });
};

export const useCreateInvitation = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvitationDto) =>
      vaultService.createInvitation(vaultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.invitations(vaultId),
      });
      toast.success(
        t('vault.messages.inviteSuccess', 'Invitation envoyée avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.inviteError',
          "Erreur lors de l'envoi de l'invitation"
        );
      toast.error(message);
    },
  });
};

export const useRevokeInvitation = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) =>
      vaultService.revokeInvitation(vaultId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.invitations(vaultId),
      });
      toast.success(
        t(
          'vault.messages.revokeInvitationSuccess',
          'Invitation révoquée avec succès'
        )
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.revokeInvitationError',
          "Erreur lors de la révocation de l'invitation"
        );
      toast.error(message);
    },
  });
};

export const useResendInvitation = (vaultId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      invitationId,
      email,
    }: {
      invitationId: string;
      email?: string;
    }) => vaultService.resendInvitation(vaultId, invitationId, email),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.invitations(vaultId),
      });
      toast.success(
        t(
          'vault.messages.resendInvitationSuccess',
          'Invitation renvoyée avec succès'
        )
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.resendInvitationError',
          "Erreur lors du renvoi de l'invitation"
        );
      toast.error(message);
    },
  });
};

// ===== Audit Logs =====

export const useVaultAuditLogs = (
  vaultId: string,
  params?: VaultAuditLogParams
) => {
  return useQuery({
    queryKey: [...vaultKeys.auditLogs(vaultId), params],
    queryFn: async () => {
      const response = await vaultService.listAuditLogs(vaultId, params);
      return response.data;
    },
    enabled: !!vaultId,
  });
};

export const useVerifyAuditChain = (vaultId: string) => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => vaultService.verifyAuditChain(vaultId),
    onSuccess: response => {
      if (response.data.valid) {
        toast.success(
          t(
            'vault.messages.auditChainValid',
            "L'intégrité de la chaîne d'audit est vérifiée"
          )
        );
      } else {
        toast.error(
          t(
            'vault.messages.auditChainInvalid',
            "Attention: la chaîne d'audit est compromise"
          )
        );
      }
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.verifyAuditError',
          "Erreur lors de la vérification de l'audit"
        );
      toast.error(message);
    },
  });
};

export const useExportAuditLogs = (vaultId: string) => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (format: 'json' | 'csv') =>
      vaultService.exportAuditLogs(vaultId, format),
    onSuccess: (blob, format) => {
      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${vaultId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(
        t('vault.messages.exportSuccess', 'Export téléchargé avec succès')
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t('vault.messages.exportError', "Erreur lors de l'export des logs");
      toast.error(message);
    },
  });
};

// ===== Contextual Vault Hooks (Folder & Employee) =====

/**
 * Get vault for a specific folder
 */
export const useFolderVault = (folderId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: vaultKeys.folder(folderId),
    queryFn: async () => {
      const response = await vaultService.getFolderVault(folderId);
      return response.data;
    },
    enabled: enabled && !!folderId,
  });
};

/**
 * Create vault for a folder
 */
export const useCreateFolderVault = (folderId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVaultDto) =>
      vaultService.createFolderVault(folderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vaultKeys.folder(folderId) });
      queryClient.invalidateQueries({ queryKey: vaultKeys.lists() });
      toast.success(
        t(
          'vault.messages.createFolderSuccess',
          'Coffre-fort du dossier créé avec succès'
        )
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.createFolderError',
          'Erreur lors de la création du coffre-fort du dossier'
        );
      toast.error(message);
    },
  });
};

/**
 * Get vault for a specific employee
 */
export const useEmployeeVault = (
  employeeId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: vaultKeys.employee(employeeId),
    queryFn: async () => {
      const response = await vaultService.getEmployeeVault(employeeId);
      return response.data;
    },
    enabled: enabled && !!employeeId,
  });
};

/**
 * Create vault for an employee
 */
export const useCreateEmployeeVault = (employeeId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVaultDto) =>
      vaultService.createEmployeeVault(employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: vaultKeys.employee(employeeId),
      });
      queryClient.invalidateQueries({ queryKey: vaultKeys.lists() });
      toast.success(
        t(
          'vault.messages.createEmployeeSuccess',
          "Coffre-fort de l'employé créé avec succès"
        )
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        t(
          'vault.messages.createEmployeeError',
          "Erreur lors de la création du coffre-fort de l'employé"
        );
      toast.error(message);
    },
  });
};

// ===== Activity Hook (for CREATOR role - history without content access) =====

/**
 * Hook pour récupérer l'historique des dépôts d'un vault
 * Utilisé par les gestionnaires (CREATOR) pour voir les métadonnées sans accès au contenu
 */
export const useVaultActivity = (
  vaultId: string,
  enabled: boolean = true,
  params?: { page?: number; limit?: number }
) => {
  return useQuery({
    queryKey: [...vaultKeys.activity(vaultId), params],
    queryFn: async () => {
      const response = await vaultService.getVaultActivity(vaultId, params);
      return response.data;
    },
    enabled: enabled && !!vaultId,
  });
};
